import { createContext, useContext, type ReactNode } from 'react';

import { useSpace, useSpaces } from '@/hooks/queries/useSpaces';
import { usePathname } from '@/lib/router';
import { useSpacesEnabled } from '@/lib/spaces/flag';
import { spaceIdFromPath } from '@/lib/spaces/navigation';
import type { SpaceDetail, SpaceSummary } from '@/types/spaces';

interface SpaceScope {
  enabled: boolean;
  scope: 'personal' | 'space';
  spaceId: string | null;
  space: SpaceDetail | null;
  spaces: SpaceSummary[];
  loading: boolean;
  listLoading: boolean;
  error: Error | undefined;
  listError: Error | undefined;
  retry: () => void;
  retryList: () => void;
}

const SpaceScopeContext = createContext<SpaceScope>({
  enabled: false, scope: 'personal', spaceId: null, space: null, spaces: [],
  loading: false, listLoading: false, error: undefined, listError: undefined,
  retry: () => {}, retryList: () => {},
});

export function SpaceScopeProvider({ children }: { children: ReactNode }): ReactNode {
  const enabled = useSpacesEnabled();
  const pathname = usePathname();
  const spaceId = spaceIdFromPath(pathname);
  const list = useSpaces(enabled);
  const detail = useSpace(spaceId, enabled);
  // Fail closed on membership revalidation errors, even if SWR retains stale data.
  const space = enabled && !detail.error && detail.data?.id === spaceId ? detail.data : null;

  const retry = () => { void detail.mutate(); };
  const retryList = () => { void list.mutate(); };

  return (
    <SpaceScopeContext.Provider value={{
      enabled, scope: spaceId ? 'space' : 'personal', spaceId, space,
      spaces: enabled && !list.error ? list.data ?? [] : [],
      loading: detail.isLoading, listLoading: list.isLoading,
      error: detail.error, listError: list.error,
      retry, retryList,
    }}>
      {children}
    </SpaceScopeContext.Provider>
  );
}

// Re-provides the scope with another view of the SAME space for everything below (the partner preview shows the
// space with the viewer's own actions put away). Everything else in the scope is passed through untouched.
export function SpaceScopeSpaceOverride({ space, children }: { space: SpaceDetail; children: ReactNode }): ReactNode {
  const scope = useContext(SpaceScopeContext);
  return <SpaceScopeContext.Provider value={{ ...scope, space }}>{children}</SpaceScopeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSpaceScope(): SpaceScope {
  return useContext(SpaceScopeContext);
}
